// The box "Reply to <name>" opens (actions contract row 1; REQ-2026-09-23-04). Mike's choice, 2026-09-23:
// "A box opens under the card. You type, press Send, and exactly what you typed goes into that Teams chat,
// or into the email thread for an email. No AI writing. The card then clears."
//
// Actions contract row 2, as Mike settled it on 2026-09-24 (answer 28, "Start with 'Cobrain,'"): the box is the plain
// "Reply": "my own words go as typed, and a message starting "Cobrain," is an instruction to draft". While the text
// starts with "Cobrain," the button says "Write it" and pressing it asks for the draft instead of sending.
//
// Opening the box records nothing (D60): the decision is written by the API only once the reply has gone
// (POST /api/triage/items/:id/reply). A send that is refused keeps the box open with the text in it and
// the refusal under it, in the server's words, so the text can be sent again or copied across.
import { OptionButton, ActionButton, ActionTextarea } from '../parts/index.js';
import { useEffect, useRef, useState } from 'react';
import { instructionOf } from './api.js';

export interface TypedReplyProps {
  /** One line saying where it is going, as the instruction box says it: "To Ryan Walsh, in Teams. You press Send." */
  going: string;
  initialText?: string;
  sendLabel?: string;
  neutral?: boolean;
  busy?: boolean;
  /** The refusal of the last Send, in the server's words; the text stays in the box. */
  error?: string | null;
  onSend: (text: string) => void;
  onCancel: () => void;
}

export function TypedReply({ going, busy = false, error = null, onSend, onCancel, initialText = '', sendLabel = 'Send', neutral = false }: TypedReplyProps) {
  const [text, setText] = useState(initialText);
  const box = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { box.current?.focus(); }, []);
  useEffect(() => { setText(initialText); }, [initialText]);
  const empty = text.trim().length === 0;
  const instruction = instructionOf(text) !== null;
  const send = () => { if (!busy && !empty) onSend(text); };

  return (
    <div className="state" data-state="typed">
      <p className="rule">{going}</p>
      <ActionTextarea
        ref={box}
        className="draft"
        aria-label="your reply"
        value={text}
        disabled={busy}
        rows={3}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          // Enter alone makes a new line: a reply is written, not searched for.
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
          if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        }}
      />
      <div className="teach-block">
        <OptionButton variant={neutral ? 'plain' : 'lead'} className="go" disabled={busy || empty} onClick={send}>{instruction ? 'Write it' : sendLabel}</OptionButton>
        <ActionButton reading type="button" className="link" disabled={busy} onClick={onCancel}>Cancel</ActionButton>
      </div>
      <p className="note">{instruction ? 'Nothing is sent. Cobrain writes it and you read it first.' : 'Your words go exactly as typed. Start with “Cobrain,” and Cobrain writes it for you to read first.'}</p>
      {error ? <p className="error" role="alert">{error}</p> : null}
    </div>
  );
}
